<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import parseStringPrice from "@/helpers/parseStringPrice";
import apiAutopay from "@/hooks/requests/apiAutopay/apiAutopay";
import apiAutopayCheckStatus from "@/hooks/requests/apiAutopay/apiAutopayCheckStatus";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";
import ArrowIcon from "@/ui/icons/arrow.vue";

interface DataFromUrl {
    payment: "success" | "failed";
    ServiceId: string;
    OrderID: string;
    Hash: string;
}

const TIWStore = useTIWStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();

const errorSentAutopayToken = ref<string>();
const payWindow = ref<Window | null>(null);
const dataFromUrl = ref<DataFromUrl>();
const isIOS = ref(false);
const isSafariDesktop = ref(false);
const url = ref<string>();

let widget: WidgetConnectionInterface = null;
const isDisabled = ref<boolean>(true);
const retry = ref<number>(10);

async function handlerOnCloseWindow() {
    console.log(retry.value);
    const response = await apiAutopayCheckStatus({
        deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
        token: TIWStore.WIDGET_DATA.token,
    });
    if (response.success) {
        TIWStore.switchStep("creditCartPreAuthorizationSuccess");
        return;
    }

    if (response.autopayStatus.status === "PENDING" && retry.value !== 0) {
        await new Promise((res) => {
            return setTimeout(() => res(undefined), 5000);
        });
        handlerOnCloseWindow();
        retry.value -= 1;
        return;
    }

    retry.value = 10;
    TIWStore.switchStep("creditCartPreAuthorizationFailed");
}

function messageListener(event: MessageEvent) {
    if (
        event.origin === "https://stg-trade-in.breezy.pl" ||
        event.origin === "https://trade-in.breezy.pl" ||
        event.origin === "http://localhost:5173"
    ) {
        dataFromUrl.value = event.data as DataFromUrl;
        isDisabled.value = false;
    }
}

function openPopupCrossBrowser(url: string, options: { width?: number; height?: number } = {}) {
    window.addEventListener("message", messageListener);

    const { width = 500, height = 600 } = options;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

    payWindow.value = window.open(url, "_blank", features);
}

watch(
    dataFromUrl,
    () => {
        if (dataFromUrl.value) {
            window.removeEventListener("message", messageListener);
        }
    },
    { deep: true },
);

watch(payWindow, (newVal) => {
    if (newVal) {
        const interval = setInterval(() => {
            if (newVal.closed) {
                payWindow.value = null;
                isDisabled.value = false;
                handlerOnCloseWindow();
                clearInterval(interval);
            }
        }, 500);
    }
});

const devicePrice = computed(() => {
    if (typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object") {
        return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale;
    }
    return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
});

async function sentAutopayToken(paymentToken: string) {
    isDisabled.value = true;
    const response = await apiAutopay({
        token: TIWStore.WIDGET_DATA.token,
        deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
        amount: parseStringPrice(devicePrice.value).toFixed(2),
        paymentToken,
    });
    if (response?.error || response.internal.error) {
        errorSentAutopayToken.value = response.error;
        TIWStore.switchStep("creditCartPreAuthorizationFailed");
        return;
    }
    url.value = response.url;
    if (!isIOS.value && !isSafariDesktop.value) {
        openPopupCrossBrowser(response.url);
    }
}

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load === ${src}`));
        document.head.appendChild(script);
    });
}

function removeWidget() {
    if (!widget) {
        return;
    }
    widget.stopConnection();
}

function submitForm(event: Event) {
    event.preventDefault();
    if (!widget || widget.invalid) {
        return;
    }
    isDisabled.value = true;
    widget.sendForm();
}

async function startWidget(widgetVariantUrl: string, widgetConfig: WidgetConfig) {
    if (!widgetEvents || !WidgetConnection) {
        return;
    }
    const iframeEl = document.getElementById("iframe") as HTMLIFrameElement;
    iframeEl.src = import.meta.env.VITE_AUTOPAY_API + widgetVariantUrl;
    widget = new WidgetConnection(widgetConfig);

    await widget.startConnection(iframeEl);
    widget.on(widgetEvents.formSuccess, (message) => {
        sentAutopayToken(message);
    });
    widget.on(widgetEvents.validityStatus, (_, eventData) => {
        if (eventData.valid) {
            isDisabled.value = false;
        } else {
            isDisabled.value = true;
        }
    });

    widget.on(widgetEvents.formError, () => {
        TIWStore.switchStep("creditCartPreAuthorizationFailed");
    });

    widget.on(widgetEvents.validationResult, (_, eventData) => {
        if (eventData.valid) {
            isDisabled.value = false;
        } else {
            isDisabled.value = true;
        }
    });

    widget.on(widgetEvents.showModal, () => {
        console.log("show modal");
    });
}

function initWidget() {
    removeWidget();
    isDisabled.value = true;
    startWidget("/widget-new/partner", {
        language: TIWStore?.standardizedLocale || "pl",
        amount: parseStringPrice(devicePrice.value),
        currency: HowYouRateDeviceStore?.registeringDeviceData?.currency || "PLN",
        serviceId: Number(import.meta.env.VITE_AUTOPAY_SERVICE_ID),
    });
}

onMounted(() => {
    isIOS.value = /iPad|iPhone|iPod/.test(navigator.userAgent);
    isSafariDesktop.value =
        /^(?:(?!chrome|android).)*safari/i.test(navigator.userAgent) && !isIOS.value;
});

onMounted(async () => {
    await loadScript(import.meta.env.VITE_WIDGET_AUTOPAY_URL);
    initWidget();
});

onBeforeUnmount(() => {
    removeWidget();
});

function onBack() {
    TIWStore.switchStep("creditCartPreAuthorization");
}
</script>

<template>
    <div class="tiw__autopay__container">
        <form novalidate @submit="submitForm($event)">
            <div id="iframe-wrapper" class="form-group">
                <iframe id="iframe" class="tiw__autopay__iframe" />
            </div>
            <div class="tiw__autopay__actions">
                <Button
                    type="button"
                    variant="skeleton"
                    class="tiw__step__chooseSigningType__btn-back"
                    @click="onBack"
                >
                    <ArrowIcon />
                    <span>{{ useTranslate("tiw__back") }}</span>
                </Button>
                <Button
                    v-if="isIOS || isSafariDesktop ? (isIOS || isSafariDesktop) && !url : true"
                    id="button"
                    :disabled="isDisabled"
                    type="submit"
                >
                    <!-- FIXME перевод -->
                    Dodaj kartę
                </Button>
                <Button v-else id="button" type="submit" @click="openPopupCrossBrowser(url)">
                    {{ useTranslate("tiw__confirm") }}
                </Button>
            </div>
        </form>
    </div>
</template>

<style scoped src="./autopay.scss" />
